// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { convertApiRoleToUiRole } from '../utils/roleUtils';
import { useNavigate } from "react-router";


interface User {
  id: string | number;
  name: string;
  email: string;
  role: string; // Utiliser string au lieu d'un type union restrictif pour supporter les rôles convertis
  token: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean; // Ajout de isAuthenticated à l'interface
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // État pour suivre l'authentification
  const [isLoading, setIsLoading] = useState(true);
  let navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Erreur lors de la lecture des données utilisateur:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        navigate('/login');
      }
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      navigate('/login');
    }

    setIsLoading(false);
  }, [navigate]);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        // L'API retourne "name" et "roles" (tableau)
        const apiUser = data.user;

        // Créer l'objet utilisateur à partir des données de l'API
        const userData = {
          id: apiUser.id,
          name: apiUser.nom || apiUser.name || '',
          email: apiUser.email,
          role: apiUser.role || (apiUser.roles && apiUser.roles[0]) || 'acheteur',
          token: data.token,
        };

        // Convertir le rôle API en rôle UI pour l'affichage
        const userWithConvertedRole = {
          ...userData,
          role: convertApiRoleToUiRole(userData.role)
        };

        // Sauvegarde en localStorage et mise à jour des états
        localStorage.setItem('user', JSON.stringify(userWithConvertedRole));
        localStorage.setItem('token', data.token);

        // Mise à jour des états React
        setUser(userWithConvertedRole);
        setIsAuthenticated(true);
      } else {
        throw new Error(data.message || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false); // Mettre à jour l'état d'authentification
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const value = {
    user,
    isAuthenticated, // Exposer isAuthenticated dans le contexte
    login,
    logout,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;